// Alarm raises over time (2026-10-30), for the Alarms Dashboard.
// mode "dots":  one dot per raise at its exact time. data [{ t, value, label }].
//               numeric: the dot sits at its value and each of `thresholds` is
//               a dashed line; otherwise every dot sits on one line (y = 1 on a
//               hidden axis). The tooltip shows each item's `label`.
// mode "lines": one line per priority. data [{ t, CRITICAL, MAJOR, MINOR,
//               WARNING }], one row per `interval` ("hour" or "day").
// t is epoch milliseconds. start and end (optional, epoch ms) fix the x-axis
// to the chosen range. The root lives on a ref'd div, is rebuilt when the
// inputs change and disposed in the same effect, so several charts can live
// on one page.
import React, { useLayoutEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

// the dashboard's priority colours (Charts/Stacked Bar Chart)
const PRIORITIES = [
  { name: "Critical", field: "CRITICAL", color: 0xbf3535 },
  { name: "Major", field: "MAJOR", color: 0x844204 },
  { name: "Minor", field: "MINOR", color: 0xfe9f1b },
  { name: "Warning", field: "WARNING", color: 0x3399ff },
];

export default function AlarmHistoryChart({
  mode,
  data,
  height,
  thresholds,
  numeric,
  interval,
  start,
  end,
}) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const root = am5.Root.new(ref.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        layout: root.verticalLayout,
      })
    );

    const lines = mode === "lines";
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: lines ? interval || "hour" : "minute", count: 1 },
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 60 }),
        tooltip: am5.Tooltip.new(root, {}),
        tooltipDateFormat:
          lines && interval === "day" ? "dd/MM/yyyy" : "dd/MM/yyyy HH:mm",
      })
    );
    xAxis.get("renderer").labels.template.setAll({ fontSize: 11 });
    if (start !== undefined && end !== undefined) {
      xAxis.setAll({ min: start, max: end });
    }

    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.labels.template.setAll({ fontSize: 11 });
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, { renderer: yRenderer })
    );

    // the DateAxis needs its data in time order
    const rows = (data || [])
      .map((item) =>
        lines ? item : { ...item, y: numeric ? Number(item.value) : 1 }
      )
      .sort((a, b) => a.t - b.t);

    if (lines) {
      yAxis.setAll({ min: 0, maxPrecision: 0 });
      PRIORITIES.forEach(({ name, field, color }) => {
        const series = chart.series.push(
          am5xy.LineSeries.new(root, {
            name,
            xAxis,
            yAxis,
            valueXField: "t",
            valueYField: field,
            stroke: am5.color(color),
            fill: am5.color(color),
            tooltip: am5.Tooltip.new(root, { labelText: "{name}: {valueY}" }),
          })
        );
        series.strokes.template.setAll({ strokeWidth: 2 });
        // a dot on every point, so a single point (a range inside one hour)
        // still shows (code review 2026-10-30)
        series.bullets.push(() =>
          am5.Bullet.new(root, {
            sprite: am5.Circle.new(root, {
              radius: 3,
              fill: series.get("fill"),
            }),
          })
        );
        series.data.setAll(rows);
        series.appear(1000);
      });
      const legend = chart.children.push(
        am5.Legend.new(root, { centerX: am5.p50, x: am5.p50 })
      );
      legend.labels.template.setAll({ fontSize: 12 });
      legend.data.setAll(chart.series.values);
      chart.set("cursor", am5xy.XYCursor.new(root, { behavior: "none" }));
    } else {
      if (numeric) {
        // keep every threshold inside the axis, not only the values
        const values = rows.map((r) => r.y).concat(thresholds || []);
        if ((thresholds || []).length && values.length) {
          const low = Math.min(...values);
          const high = Math.max(...values);
          const pad = (high - low) * 0.1 || 1;
          yAxis.setAll({ min: low - pad, max: high + pad });
        }
        (thresholds || []).forEach((value) => {
          const range = yAxis.createAxisRange(yAxis.makeDataItem({ value }));
          range.get("grid").setAll({
            stroke: am5.color(0xbf3535),
            strokeOpacity: 1,
            strokeWidth: 1,
            strokeDasharray: [4, 4],
          });
          range.get("label").setAll({
            text: `Threshold ${value}`,
            inside: true,
            // starts at the axis and runs right, so it is not cut off at the chart's left edge (2026-10-30)
            centerX: am5.percent(0),
            centerY: am5.p100,
            fontSize: 11,
            fill: am5.color(0xbf3535),
          });
        });
      } else {
        yAxis.setAll({ min: 0, max: 2, strictMinMax: true });
        yRenderer.labels.template.set("forceHidden", true);
        yRenderer.grid.template.set("forceHidden", true);
      }

      const series = chart.series.push(
        am5xy.LineSeries.new(root, {
          xAxis,
          yAxis,
          valueXField: "t",
          valueYField: "y",
        })
      );
      // dots only, no line between them
      series.strokes.template.setAll({ strokeOpacity: 0 });
      series.bullets.push(() =>
        am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 5,
            fill: series.get("fill"),
            tooltipText: "{label}",
          }),
        })
      );
      series.data.setAll(rows);
      series.appear(1000);
    }

    chart.appear(1000, 100);

    return () => root.dispose();
  }, [mode, data, thresholds, numeric, interval, start, end]);

  return <div ref={ref} style={{ width: "100%", height }} />;
}
